import {
  Box,
  Link,
  ListItem,
  OrderedList,
  Text,
  UnorderedList,
} from '@chakra-ui/react';
import type { Components } from 'react-markdown';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import { colors } from '@/theme/foundations/colors';
import type { MarkdownSubtextProps } from './MarkdownSubtext.interface';

function createMarkdownComponents(
  bodyColor: string,
  fontSize: string
): Partial<Components> {
  return {
    p: ({ children }) => (
      <Text
        as="p"
        fontSize={fontSize}
        color={bodyColor}
        lineHeight="1.5"
        mb={2}
        _last={{ mb: 0 }}
      >
        {children}
      </Text>
    ),
    a: ({ href, children }) => (
      <Link
        href={href && href.length > 0 ? href : '#'}
        isExternal
        color={colors['primary-500']}
        textDecoration="underline"
        fontSize={fontSize}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </Link>
    ),
    br: () => <br />,
    strong: ({ children }) => (
      <Text as="strong" fontWeight="semibold">
        {children}
      </Text>
    ),
    em: ({ children }) => (
      <Text as="em" fontStyle="italic">
        {children}
      </Text>
    ),
    ul: ({ children }) => (
      <UnorderedList
        pl={4}
        mb={2}
        fontSize={fontSize}
        color={bodyColor}
        spacing={1}
      >
        {children}
      </UnorderedList>
    ),
    ol: ({ children }) => (
      <OrderedList
        pl={4}
        mb={2}
        fontSize={fontSize}
        color={bodyColor}
        spacing={1}
      >
        {children}
      </OrderedList>
    ),
    li: ({ children }) => (
      <ListItem lineHeight="1.5">{children}</ListItem>
    ),
  };
}

/** Renders markdown help/subtext with GFM URL autolinking and Chakra styling. */
export function MarkdownSubtext({
  markdown,
  bodyColor = 'secondary-500',
  fontSize = '14px',
  ...boxProps
}: MarkdownSubtextProps) {
  const source = markdown == null ? '' : String(markdown);
  if (!source) return null;

  const components = createMarkdownComponents(bodyColor, fontSize);

  return (
    <Box {...boxProps}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={components}
      >
        {source}
      </ReactMarkdown>
    </Box>
  );
}
